import * as Video from 'twilio-video';
import { JSONRPCServer, JSONRPCClient } from 'json-rpc-2.0';
import {
  PROTOCOL_RPC_METHODS,
  RpcCallPayload,
  RpcCommandPayload,
  SignalData
} from './protocol/automatedParticipantProtocol';
import { Lobby, RingerStopReason } from '@lib/Lobby';

export interface SignalingEvents {
  // Clinician side handlers
  onCallOffer: (senderId: string, callId: string) => void;
  onCallCancelled: (senderId: string, callId: string) => void;
  onCallAccepted: (senderId: string, callId: string) => void;
  onCallDeclined: (senderId: string, callId: string) => void;
  onPromptVideoComplete?: (senderId: string, data?: SignalData) => void;
  onRecordingStarted?: (senderId: string, data?: SignalData) => void;
  onRecordingStopped?: (senderId: string, data?: SignalData) => void;
  onReplayComplete?: (senderId: string, data?: SignalData) => void;
  onThankYouVideoComplete?: (senderId: string, data?: SignalData) => void;
  onRequestPromptVideo?: (senderId: string, data?: SignalData) => void;
  // Patient side handlers
  onOfferToStart: (payload: RpcCallPayload) => void | Promise<void>;
  onOfferToStartCancelled: (payload: RpcCallPayload) => void | Promise<void>;
  onStartRecording: (payload: RpcCommandPayload) => void | Promise<void>;
  onCallTerminated: (payload: RpcCommandPayload) => void | Promise<void>;
  onStopRecording: (payload: RpcCommandPayload) => void | Promise<void>;
  onReplayRecording: (payload: RpcCommandPayload) => void | Promise<void>;
  onEndRecordingSession: (payload: RpcCommandPayload) => void | Promise<void>;
}

export class CallManager {
  private readonly server = new JSONRPCServer();
  private readonly client = new JSONRPCClient((request) => {
    if (this.remoteTrackCleanups.size === 0) {
      throw new Error('No remote participant connected');
    }
    console.log('DataTrack send:', request);
    this.localDataTrack.send(JSON.stringify(request));
    return Promise.resolve();
  });
  private currentCallId: string | null = null;
  private readonly listenerCleanup: Array<() => void> = [];
  private readonly remoteTrackCleanups = new Map<string, () => void>();
  private readonly lobby: Lobby;
  private pendingOfferPayload: RpcCallPayload | null = null;
  private readonly delaySubscribers = new Set<(delayMinutes: number) => void>();

  constructor(
    private readonly room: Video.Room,
    private readonly localDataTrack: Video.LocalDataTrack,
    private eventHandlers: Partial<SignalingEvents> = {}
  ) {
    this.lobby = Lobby.forRoom(room);

    this.server.addMethod(PROTOCOL_RPC_METHODS.patient.incoming.OFFER_TO_START, (payload: RpcCallPayload) => {
      this.currentCallId = payload.callId;
      this.lobby.callStatus = 'calling';
      this.lobby.lastRingerStopReason = '';
      if (this.eventHandlers.onCallOffer) {
        this.eventHandlers.onCallOffer(payload.senderId, payload.callId);
      } else {
        console.log('[CallManager] Offer received before handler set, queueing...');
        this.pendingOfferPayload = payload;
      }
      return this.eventHandlers.onOfferToStart?.(payload);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.patient.incoming.OFFER_TO_START_CANCELLED, (payload: RpcCallPayload) => {
      this.eventHandlers.onCallCancelled?.(payload.senderId, payload.callId);
      this.lobby.callStatus = 'not-started';
      this.lobby.lastRingerStopReason = 'cancelled';
      return this.eventHandlers.onOfferToStartCancelled?.(payload);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.clinician.incoming.OFFER_ACCEPTED, ({ callId, senderId }) => {
      this.eventHandlers.onCallAccepted?.(senderId, callId);
      this.lobby.callStatus = 'in-progress';
      this.lobby.lastRingerStopReason = '';
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.clinician.incoming.CALL_DECLINED, ({ callId, senderId }) => {
      this.eventHandlers.onCallDeclined?.(senderId, callId);
      this.lobby.callStatus = 'not-started';
      this.lobby.lastRingerStopReason = 'declined';
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.clinician.incoming.PROMPT_VIDEO_COMPLETE, ({ senderId, data }) => {
      this.eventHandlers.onPromptVideoComplete?.(senderId, data);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.clinician.incoming.RECORDING_STARTED, ({ senderId, data }) => {
      this.eventHandlers.onRecordingStarted?.(senderId, data);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.clinician.incoming.RECORDING_STOPPED, ({ senderId, data }) => {
      this.eventHandlers.onRecordingStopped?.(senderId, data);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.clinician.incoming.REPLAY_COMPLETE, ({ senderId, data }) => {
      this.eventHandlers.onReplayComplete?.(senderId, data);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.clinician.incoming.THANK_YOU_VIDEO_COMPLETE, ({ senderId, data }) => {
      this.eventHandlers.onThankYouVideoComplete?.(senderId, data);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.clinician.incoming.REQUEST_PROMPT_VIDEO, ({ senderId, data }) => {
      this.eventHandlers.onRequestPromptVideo?.(senderId, data);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.patient.incoming.START_RECORDING, (payload: RpcCommandPayload) => {
      return this.eventHandlers.onStartRecording?.(payload);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.patient.incoming.TERMINATE, (payload: RpcCommandPayload) => {
      this.lobby.callStatus = 'completed';
      return this.eventHandlers.onCallTerminated?.(payload);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.patient.incoming.STOP_RECORDING, (payload: RpcCommandPayload) => {
      return this.eventHandlers.onStopRecording?.(payload);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.patient.incoming.REPLAY_RECORDING, (payload: RpcCommandPayload) => {
      return this.eventHandlers.onReplayRecording?.(payload);
    });

    this.server.addMethod(PROTOCOL_RPC_METHODS.patient.incoming.END_RECORDING_SESSION, (payload: RpcCommandPayload) => {
      return this.eventHandlers.onEndRecordingSession?.(payload);
    });

    this.server.addMethod(
      PROTOCOL_RPC_METHODS.patient.incoming.DELAY_APPOINTMENT,
      (payload: { delayMinutes: number }) => {
        this.delaySubscribers.forEach((handler) => handler(payload.delayMinutes));
      }
    );

    const setupParticipantDataTracks = (participant: Video.RemoteParticipant) => {
      participant.tracks.forEach((pub) => {
        if (pub.track?.kind === 'data') {
          this.attachRemoteDataTrack(pub.track);
        }
      });

      const handleTrackSubscribed = (track: Video.RemoteTrack) => {
        if (track.kind === 'data') {
          this.attachRemoteDataTrack(track);
        }
      };

      const handleTrackUnsubscribed = (track: Video.RemoteTrack) => {
        if (track.kind === 'data') {
          this.detachRemoteDataTrack(track.sid);
        }
      };

      participant.on('trackSubscribed', handleTrackSubscribed);
      participant.on('trackUnsubscribed', handleTrackUnsubscribed);

      this.listenerCleanup.push(() => {
        participant.removeListener('trackSubscribed', handleTrackSubscribed);
        participant.removeListener('trackUnsubscribed', handleTrackUnsubscribed);
      });
    };

    const onParticipantConnected = (participant: Video.RemoteParticipant) => {
      setupParticipantDataTracks(participant);
    };

    this.room.on('participantConnected', onParticipantConnected);
    this.listenerCleanup.push(() => this.room.removeListener('participantConnected', onParticipantConnected));
    const onParticipantDisconnected = (participant: Video.RemoteParticipant) => {
      participant.tracks.forEach((pub) => {
        if (pub.track?.kind === 'data') {
          this.detachRemoteDataTrack(pub.track.sid);
        }
      });
      if (this.lobby.callStatus === 'calling') {
        this.lobby.callStatus = 'not-started';
      }
    };
    this.room.on('participantDisconnected', onParticipantDisconnected);
    this.listenerCleanup.push(() => this.room.removeListener('participantDisconnected', onParticipantDisconnected));

    this.room.participants.forEach(setupParticipantDataTracks);
  }

  private checkRemoterParticipant(msg = 'complete operation') {
    if (this.room.participants.size === 0) {
      throw new Error(`Cannot ${msg}: no remote participant connected`);
    }
  }

  public terminateCall(data?: SignalData) {
    // NB: the patient does not need to be present to terminate a call
    // this.checkRemoterParticipant('terminate call');

    if (!this.currentCallId) this.currentCallId = Date.now().toString();

    const payload = this.buildCallPayload(data);

    this.lobby.callStatus = 'completed';
    return this.client.request(PROTOCOL_RPC_METHODS.clinician.outgoing.TERMINATE, payload);
  }
  /**
   * Returns true if remote data tracks are available for communication.
   */
  public hasRemoteDataTracks(): boolean {
    return this.remoteTrackCleanups.size > 0;
  }

  /**
   * Sends an offer to start a call to the remote participant.
   *
   * This is triggered by the clinician when the patient joins the room.
   *
   * The remote side will respond via either:
   *  - `onCallAccepted` handler
   *  - `onCallDeclined` handler (not for test patient)
   *
   * Calling this method does NOT wait for the response; it only sends
   * the signaling message.
   *
   */
  public offerToStart(data?: SignalData) {
    this.checkRemoterParticipant('start call');

    if (!this.currentCallId) this.currentCallId = Date.now().toString();

    const payload = this.buildCallPayload(data);

    this.lobby.callStatus = 'calling';
    this.lobby.lastRingerStopReason = '';
    return this.client.request(PROTOCOL_RPC_METHODS.clinician.outgoing.OFFER_TO_START, payload);
  }

  /**
   * Sends a message accepting an incoming call offer previously received from the clinician.
   *
   * This is triggered by the patient when they have received the call offer
   * from the clincian's' datatrack (after a 3 second wait).
   *
   */
  public acceptOffer(data?: SignalData) {
    if (!this.currentCallId) return;
    this.lobby.callStatus = 'in-progress';
    this.lobby.lastRingerStopReason = '';
    return this.client.request(PROTOCOL_RPC_METHODS.patient.outgoing.OFFER_ACCEPTED, this.buildCallPayload(data));
  }

  /**
   * Sends a message to cancel the call after the offer to start has been sent.
   *
   * This is triggered by the clinician manually to either cancel the call before it
   * has started or whilst it is in-progress (end the call, can be seperated out).
   *
   */
  public cancelCall(stopReason: RingerStopReason = 'cancelled', data?: SignalData) {
    if (!this.currentCallId) return;
    this.lobby.callStatus = 'not-started';
    this.lobby.lastRingerStopReason = stopReason;
    return this.client.request(
      PROTOCOL_RPC_METHODS.clinician.outgoing.OFFER_TO_START_CANCELLED,
      this.buildCallPayload(data)
    );
  }

  /**
   * Sends a message to decline the incoming call offer sent from the clinician.
   *
   * Not possible for automated patient, but would be triggered by the patient
   * manually declining the call.
   *
   */
  public declineCall(data?: SignalData) {
    if (!this.currentCallId) return;
    this.lobby.callStatus = 'not-started';
    this.lobby.lastRingerStopReason = 'declined';
    return this.client.request(PROTOCOL_RPC_METHODS.patient.outgoing.CALL_DECLINED, {
      callId: this.currentCallId,
      senderId: this.room.localParticipant.sid,
      data
    });
  }

  public startRecording(data?: SignalData) {
    return this.client.request(PROTOCOL_RPC_METHODS.clinician.outgoing.START_RECORDING, this.buildCommandPayload(data));
  }

  public stopRecording(data?: SignalData) {
    return this.client.request(PROTOCOL_RPC_METHODS.clinician.outgoing.STOP_RECORDING, this.buildCommandPayload(data));
  }

  public replayRecording(data?: SignalData) {
    return this.client.request(
      PROTOCOL_RPC_METHODS.clinician.outgoing.REPLAY_RECORDING,
      this.buildCommandPayload(data)
    );
  }

  public endRecordingSession(data?: SignalData) {
    return this.client.request(
      PROTOCOL_RPC_METHODS.clinician.outgoing.END_RECORDING_SESSION,
      this.buildCommandPayload(data)
    );
  }

  /**
   * Sends prompt video complete signal to clinician.
   * Patient-side method for signalling that prompt video playback completed.
   */
  public sendPromptVideoComplete(data?: SignalData) {
    return this.client.request(
      PROTOCOL_RPC_METHODS.patient.outgoing.PROMPT_VIDEO_COMPLETE,
      this.buildCommandPayload(data)
    );
  }

  /**
   * Sends recording started signal to clinician.
   * Patient-side method for signalling that recording has begun.
   */
  public sendRecordingStarted(data?: SignalData) {
    return this.client.request(PROTOCOL_RPC_METHODS.patient.outgoing.RECORDING_STARTED, this.buildCommandPayload(data));
  }

  /**
   * Sends recording stopped signal to clinician.
   * Patient-side method for signalling that recording has stopped.
   */
  public sendRecordingStopped(data?: SignalData) {
    return this.client.request(PROTOCOL_RPC_METHODS.patient.outgoing.RECORDING_STOPPED, this.buildCommandPayload(data));
  }

  /**
   * Sends replay complete notification to clinician.
   * Patient-side method for signalling that replay playback completed.
   */
  public sendReplayComplete(data?: SignalData) {
    return this.client.request(PROTOCOL_RPC_METHODS.patient.outgoing.REPLAY_COMPLETE, this.buildCommandPayload(data));
  }

  /**
   * Sends thank you video complete signal to clinician.
   * Patient-side method for signalling that thank you video playback completed.
   */
  public sendThankYouVideoComplete(data?: SignalData) {
    return this.client.request(
      PROTOCOL_RPC_METHODS.patient.outgoing.THANK_YOU_VIDEO_COMPLETE,
      this.buildCommandPayload(data)
    );
  }

  /** Requests clinician to play prompt video. */
  public requestPromptVideo(data?: SignalData) {
    return this.client.request(
      PROTOCOL_RPC_METHODS.patient.outgoing.REQUEST_PROMPT_VIDEO,
      this.buildCommandPayload(data)
    );
  }

  //  Sends delay appointment signal to patient via DataTrack.
  public delayAppointment(delayMinutes: number) {
    this.delaySubscribers.forEach((handler) => handler(delayMinutes));
    return this.client.request(PROTOCOL_RPC_METHODS.clinician.outgoing.DELAY_APPOINTMENT, { delayMinutes });
  }

  // Subscribe to delay updates.
  public subscribeToDelay(handler: (delayMinutes: number) => void): () => void {
    this.delaySubscribers.add(handler);
    return () => {
      this.delaySubscribers.delete(handler);
    };
  }

  /**
   * Allows event handlers to be overwritten, e.g. if the clinician wants
   * to start the call/perform another operation within the Clincian
   * session rather than the in that CallManager
   * that's initialised in useTestPatientRoom.
   */
  public setHandlers(handlers: Partial<SignalingEvents>) {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };

    // Replay pending offer if one was received before handlers were set
    if (this.pendingOfferPayload && handlers.onCallOffer) {
      console.log('[CallManager] Replaying pending offer...');
      handlers.onCallOffer(this.pendingOfferPayload.senderId, this.pendingOfferPayload.callId);
      this.pendingOfferPayload = null;
    }
  }

  /**
   * For cleanup purposes
   *
   * Disconnects all datatrack listeners when call is
   * cancelled/declined/ended.
   *
   */
  public disconnect() {
    this.listenerCleanup.forEach((cleanup) => {
      try {
        cleanup();
      } catch {
        // ignore cleanup errors
      }
    });
    this.listenerCleanup.length = 0;

    this.remoteTrackCleanups.forEach((cleanup) => {
      try {
        cleanup();
      } catch {
        // ignore cleanup errors
      }
    });
    this.remoteTrackCleanups.clear();
  }

  private buildCallPayload(data?: SignalData): RpcCallPayload {
    return {
      callId: this.currentCallId as string,
      senderId: this.room.localParticipant.sid,
      data
    };
  }

  private buildCommandPayload(data?: SignalData): RpcCommandPayload {
    return {
      senderId: this.room.localParticipant.sid,
      data
    };
  }

  /**
   * Attach message listeners to a remote data track and record the cleanup for later.
   */
  private attachRemoteDataTrack(track: Video.RemoteDataTrack) {
    const existingCleanup = this.remoteTrackCleanups.get(track.sid);
    existingCleanup?.();

    const handleMessage = (msg: string | ArrayBuffer) => {
      const text = typeof msg === 'string' ? msg : new TextDecoder().decode(msg);
      this.server.receiveJSON(text);
    };

    track.on('message', handleMessage);

    const cleanup = () => {
      track.removeListener('message', handleMessage);
      this.remoteTrackCleanups.delete(track.sid);
    };

    this.remoteTrackCleanups.set(track.sid, cleanup);
  }

  /**
   * Remove listeners for a previously attached remote data track.
   */
  private detachRemoteDataTrack(trackSid: string) {
    const cleanup = this.remoteTrackCleanups.get(trackSid);
    if (!cleanup) return;
    cleanup();
  }
}
